/**
 * Spanish display labels for engine-emitted codes shown to non-technical users.
 * Falls back to the raw code string for any unknown value so new codes don't
 * silently break the UI — they just render as-is until added here.
 */

export const FAILURE_CODE_LABEL: Record<string, string> = {
  // Granular intake gate causes (split out of the old catch-all INTAKE_FAILED so
  // each gets a precise label, breakdown bar, and filter facet).
  INTAKE_MISSING_DOCUMENT: "Expediente incompleto",
  INTAKE_FILE_UNREADABLE: "Archivo ilegible o dañado",
  INTAKE_FILE_TOO_LARGE: "Archivo demasiado grande",
  INTAKE_DUPLICATE_DOCUMENT: "Documento duplicado",
  INTAKE_NOT_A_FOLDER: "Carpeta de expediente no válida",
  // Legacy code retained so already-sealed audit records still render a label.
  INTAKE_FAILED: "Expediente incompleto o ilegible",
  // ADR 0009 renamed the codes that cover the identification whichever kind it
  // is (INE o pasaporte) from INE_* to IDEN_*; the INE_* keys are retained so
  // already-sealed audit records still render a label.
  IDEN_CURP_INVALID: "CURP de la identificación inválida",
  INE_CURP_INVALID: "CURP del INE inválida",
  CERT_CURP_INVALID: "CURP del certificado inválida",
  INE_EXPIRED: "INE vencida",
  // Vigency window wider than the 10-year legal maximum (alteration signal),
  // distinct from mere expiry.
  INE_VIGENCY_INVALID: "Vigencia del INE inválida",
  INVALID_FOLIO: "Folio inválido",
  IDEN_VALIDATION_FAILED: "Identificación inconsistente (verificación interna)",
  INE_VALIDATION_FAILED: "INE inconsistente (verificación interna)",
  // ADR 0009: an expired passport rejects, symmetric with INE_EXPIRED.
  PASSPORT_EXPIRED: "Pasaporte vencido",
  DOCUMENT_QUALITY_LOW: "Certificado ilegible (baja calidad)",
  // ADR 0012: the certificado slot holds another school document (primaria,
  // boleta, constancia…) — a rejection only with the profile toggle on.
  CERT_WRONG_DOCUMENT: "Otro documento en lugar del certificado",
  // ADR 0012 (#346): a recreation without official elements.
  CERT_NOT_RECOGNIZED: "Certificado no reconocido (sin elementos oficiales)",
};

export const FLAG_CODE_LABEL: Record<string, string> = {
  FACE_CONFIDENCE_LOW: "Confianza facial baja",
  FACE_CONFIDENCE_MEDIUM: "Confianza facial media",
  FACE_COMPARISON_FAILED: "Comparación facial fallida",
  NAME_MISMATCH: "Nombre no coincide",
  CURP_MISMATCH: "CURP no coincide",
  FOLIO_PENDING: "Folio pendiente de validación",
  // ADR 0009 IDEN_* codes (new) + their INE_* predecessors (sealed records).
  IDEN_CURP_PENDING: "CURP de la identificación pendiente",
  INE_CURP_PENDING: "CURP del INE pendiente",
  CERT_CURP_PENDING: "CURP del certificado pendiente",
  IDEN_VIGENCY_PENDING: "Vigencia de la identificación pendiente",
  INE_VIGENCY_PENDING: "Vigencia del INE pendiente",
  IDEN_VALIDATION_PENDING: "Validación de identificación pendiente",
  INE_VALIDATION_PENDING: "Validación de INE pendiente",
  // #285: the MRZ checksums cleanly but its fecha de nacimiento / sexo contradict
  // the front CURP — a genuine INE that may belong to someone else.
  IDEN_MRZ_CROSS_MISMATCH: "MRZ de la identificación no coincide con la CURP",
  INE_MRZ_CROSS_MISMATCH: "MRZ del INE no coincide con la CURP",
  // ADR 0009: the expediente presents a passport and the active validation
  // profile does not accept passports as identification.
  PASSPORT_NOT_ACCEPTED: "Pasaporte no aceptado por el perfil",
  NAME_COMPARISON_PENDING: "Comparación de nombres pendiente",
  DOCUMENT_QUALITY_MARGINAL: "Calidad de documento marginal",
  CERTIFICATE_UNCLASSIFIABLE: "Certificado no clasificable",
  CERT_VALIDATION_PENDING: "Validación de certificado pendiente",
  // ADR 0006: the certificate's QR resolves to a non-official domain.
  QR_DOMINIO_SOSPECHOSO: "QR en dominio sospechoso",
  // Advisory only: a QR is present but the offline decode produced no URL, so
  // there is no domain classification to show. Never routes to review by itself.
  QR_DECODE_PENDING: "QR presente, no se pudo decodificar",
  // ADR 0012: the same finding as a HIGH flag while the profile's rejection tier
  // is off (shadow mode), or when the second signal is missing (lone signal).
  CERT_WRONG_DOCUMENT: "Otro documento en lugar del certificado",
  CERT_NOT_RECOGNIZED: "Certificado no reconocido (sin elementos oficiales)",
  // ADR 0012 review routes (#347–#351): never a rejection, whatever the toggle.
  CERT_SCREEN_CAPTURE: "Certificado en captura o foto de pantalla",
  CERT_ANOMALIES: "Anomalías visibles en el certificado",
  CERT_NOT_COLOR: "Certificado no está a color",
  CERT_PHOTO_SEAL_MISSING: "Certificado sin foto con sello",
  CERT_BEFORE_1990: "Certificado anterior a 1990",
  CERT_QR_DAMAGED: "QR del certificado dañado",
  // The certificado was read but its conformity check call failed: reviewed, not
  // approved unchecked.
  CERT_CONFORMITY_NOT_OBSERVED: "Certificado sin revisión de conformidad",
};

export const CERT_TYPE_LABEL: Record<string, string> = {
  DIGITAL_QR: "Digital con QR",
  PRINTED_MODERN: "Impreso (moderno)",
  PRINTED_LEGACY: "Impreso (histórico)",
  INEA: "INEA",
  TELESECUNDARIA: "Telesecundaria",
  PRIVATE_SCHOOL: "Escuela privada",
  UNCLASSIFIABLE: "No clasificable",
};

/** ADR 0012: what the vision model observed the certificado file to BE. */
export const CERT_TIPO_DOCUMENTO_LABEL: Record<string, string> = {
  CERTIFICADO_SECUNDARIA: "Certificado de secundaria",
  OTRO_DOCUMENTO_ESCOLAR: "Otro documento escolar",
  RECREACION_SIN_ELEMENTOS_OFICIALES: "Recreación sin elementos oficiales",
  OTRA_COSA: "Otro tipo de documento",
  NO_DETERMINABLE: "No determinable",
};

/** ADR 0012 tri-state observation (the model saw it / saw it absent / can't tell). */
export const OBSERVACION_LABEL: Record<string, string> = {
  SI: "Sí",
  NO: "No",
  NO_DETERMINABLE: "No determinable",
};

/** ADR 0012 (#347): how the certificado file was captured, as observed. */
export const CERT_FORMA_CAPTURA_LABEL: Record<string, string> = {
  ESCANEO: "Escaneo",
  FOTO_PAPEL: "Foto del papel",
  PDF_ELECTRONICO: "PDF electrónico",
  FOTOCOPIA: "Fotocopia",
  CAPTURA_PANTALLA: "Captura de pantalla",
  FOTO_PANTALLA: "Foto de pantalla",
  NO_DETERMINABLE: "No determinable",
};

/** ADR 0012 (#348): visible AI-generation / editing anomalies. */
export const CERT_ANOMALIA_LABEL: Record<string, string> = {
  TEXTO_SIN_SENTIDO: "Texto sin sentido o palabras inventadas",
  ESCUDO_LOGO_DEFORMADO: "Escudo o logo deformado",
  TEXTO_SUPERPUESTO_EDITADO: "Texto superpuesto o editado",
  CAMPOS_OFICIALES_VACIOS: "Campos oficiales vacíos",
  FECHAS_CONTRADICTORIAS: "Fechas contradictorias",
  INTERFAZ_APP_NAVEGADOR: "Interfaz de app o navegador visible",
  MARCA_AGUA_GENERADOR: "Marca de agua de generador de imágenes",
};

/** ADR 0012 (#349): whether the certificado is printed in color. */
export const CERT_COLOR_LABEL: Record<string, string> = {
  COLOR: "A color",
  GRISES: "En escala de grises",
  NO_DETERMINABLE: "No determinable",
};

/** ADR 0012 (#349): the folio's ink color (ROJO is the 2014–2016 exception). */
export const CERT_COLOR_FOLIO_LABEL: Record<string, string> = {
  ROJO: "Rojo",
  NEGRO: "Negro",
  OTRO: "Otro",
  NO_DETERMINABLE: "No determinable",
};

/** ADR 0012 (#350): the seal on the student's photo (the four forms are alternatives). */
export const CERT_SELLO_FOTO_LABEL: Record<string, string> = {
  TINTA: "Sello en tinta",
  RELIEVE: "Sello en relieve",
  SELLO_DIGITAL: "Sello digital",
  FOTO_DIGITAL: "Foto digital",
  NINGUNO: "Ninguno",
  NO_DETERMINABLE: "No determinable",
};

/** ADR 0012 (#351): the QR's visual condition (distinct from the local decode). */
export const CERT_ESTADO_QR_LABEL: Record<string, string> = {
  COMPLETO_NITIDO: "Completo y nítido",
  DANADO: "Dañado",
  SIN_QR: "Sin QR",
  NO_DETERMINABLE: "No determinable",
};

/** Returns the Spanish label for a code, falling back to the raw code. */
export function labelOrCode(
  map: Record<string, string>,
  code: string,
): string {
  return map[code] ?? code;
}
